/**
 * Press-in on every pressable (UX spec v3 §C.6; `bindPress` of the design's shared.js).
 *
 * `[data-pressed]` is set on pointerdown and held for at least `--t-press` (90 ms) so a
 * quick tap still shows the sink; it lets go on pointerup / pointercancel / dragstart,
 * or once the finger has travelled more than 10 px (that is a scroll, not a press).
 * `vault.css` §8.2 draws it: surfaces scale, rows darken. Disabled and busy controls
 * never sink, and neither does a surface that is not a control (a row that is a `div`
 * would promise an action it does not have). The mark (`.aim`) and the mic (`.mic`)
 * draw their own feedback and are not in the list.
 *
 * iOS applies `:active` only when a `touchstart` listener exists — the passive no-op
 * below (research 11 §2 item 5) keeps the CSS fallback alive; `[data-pressed]` is the
 * primary mechanism anyway.
 */
export const PRESSABLE =
  ".btn,.acct,.tile,.opt,.filters .chip,.act,.icobtn,.tab,.endline .go,.receipt .raw,.op,.item,.setrow,.persona,.more,.verified,.atk,button.jrow";

const MIN_MS = 90;
const TRAVEL = 10;

interface Target {
  addEventListener(type: string, fn: EventListener, options?: boolean | AddEventListenerOptions): void;
  removeEventListener(type: string, fn: EventListener, options?: boolean | EventListenerOptions): void;
}

export interface PressDeps {
  readonly now: () => number;
  readonly later: (fn: () => void, ms: number) => void;
}

/** A control a finger can operate: a button, a link, or an element that says it is one. */
function isControl(el: Element): boolean {
  return el.matches("button, a[href], [role='button'], [role='tab'], [role='radio'], [role='switch'], [role='link']");
}

function inert(el: Element): boolean {
  return el.getAttribute("aria-disabled") === "true" || el.getAttribute("aria-busy") === "true" || (el as HTMLButtonElement).disabled === true;
}

export function installPress(doc: Target, deps: PressDeps): () => void {
  let cur: { el: Element; t0: number; x: number; y: number } | undefined;

  const release = (): void => {
    if (cur === undefined) return;
    const { el, t0 } = cur;
    cur = undefined;
    const left = Math.max(0, MIN_MS - (deps.now() - t0));
    if (left === 0) el.removeAttribute("data-pressed");
    else deps.later(() => el.removeAttribute("data-pressed"), left);
  };

  const down = (e: Event): void => {
    const p = e as PointerEvent;
    if (p.button > 0) return;
    const target = p.target instanceof Element ? p.target.closest(PRESSABLE) : null;
    if (target === null || !isControl(target) || inert(target)) return;
    release();
    cur = { el: target, t0: deps.now(), x: p.clientX, y: p.clientY };
    target.setAttribute("data-pressed", "true");
  };

  const move = (e: Event): void => {
    const p = e as PointerEvent;
    if (cur !== undefined && Math.hypot(p.clientX - cur.x, p.clientY - cur.y) > TRAVEL) release();
  };

  const noop = (): void => undefined;
  const ends = ["pointerup", "pointercancel", "dragstart"] as const;
  doc.addEventListener("pointerdown", down, true);
  doc.addEventListener("pointermove", move, true);
  for (const t of ends) doc.addEventListener(t, release, true);
  doc.addEventListener("touchstart", noop, { passive: true });

  return () => {
    doc.removeEventListener("pointerdown", down, true);
    doc.removeEventListener("pointermove", move, true);
    for (const t of ends) doc.removeEventListener(t, release, true);
    doc.removeEventListener("touchstart", noop);
  };
}
